import type { ConversationLanguage } from '@cyberscore/contracts';

/**
 * The chat's own words around the conversation, in the conversation's language (decision 0007). What the
 * assistant says comes from the core (CHAT_COPY) and the agents; this is the buttons, labels and notes.
 * The Malay is a draft for native review, like the rest of the Malay copy.
 */
export interface ThreadCopy {
  points: (n: number) => string;
  basedOn: string;
  tapped: string;
  form: string;
  again: string;
  optional: string;
  killSwitchQuestion: string;
  killSwitchCard: string;
  stageDone: (name: string) => string;
  doneTitle: (requiredLeft: number) => string;
  doneText: (answered: number, requiredLeft: number) => string;
  quickReplies: string;
  pickAll: string;
  skip: string;
  done: (n: number) => string;
  upload: string;
  uploadLater: (optional: boolean) => string;
  /** What the question takes, e.g. "PDF, PNG image or JPEG image". */
  uploadHint: (kinds: string) => string;
  reviewDocs: (n: number, disagree: number) => string;
  typeBelow: string;
  useForm: string;
  saveAnswer: string;
  enterFirst: string;
  understood: string;
  foundIn: (doc: string) => string;
  sure: (pct: number) => string;
  page: (n: number) => string;
  rowState: { confirmed: string; declined: string; expired: string };
  confirm: string;
  confirmLabel: (own: number, together: number, many: boolean) => string;
  footer: { ownOnly: string; mixed: string; document: string; chat: string };
  change: string;
  placeholder: string;
  yourReply: string;
  send: string;
  busy: { send: string; save: string; wait: string; language: string; upload: string };
  ask: [string, string];
}

export const THREAD_COPY: Record<ConversationLanguage, ThreadCopy> = {
  en: {
    points: (n) => `${n > 0 ? '+' : '−'}${Math.abs(n)} points`,
    basedOn: 'Based on',
    tapped: 'Tapped',
    form: 'Answered in the form',
    again: 'Back to this one.',
    optional: 'Optional',
    killSwitchQuestion: 'This answer can cap your score, so it is only saved when you tap or confirm it.',
    killSwitchCard: 'Includes an answer that can cap your score, so nothing is saved until you confirm it.',
    stageDone: (name) => `${name} done`,
    doneTitle: (left) => (left ? 'Nothing more to ask right now' : 'That’s everything required'),
    doneText: (answered, left) =>
      `${answered} answered. ${left ? `${left} required questions open once earlier stages are done.` : 'Optional questions can still sharpen your result.'}`,
    quickReplies: 'Quick replies',
    pickAll: 'Pick all that apply',
    skip: 'Skip',
    done: (n) => `Done${n ? ` (${n})` : ''}`,
    upload: 'Upload a file',
    uploadLater: (optional) =>
      `Uploading files isn’t available here${optional ? '. Skip this one for now.' : '.'}`,
    uploadHint: (kinds) => `${kinds}, up to 25 MB. It’s scanned for malware before it’s kept.`,
    reviewDocs: (n, disagree) =>
      `${n} ${n === 1 ? 'answer' : 'answers'} from your documents to review${disagree ? `, ${disagree} where they disagree` : ''}`,
    typeBelow: 'Type your answer below',
    useForm: 'Use a form instead',
    saveAnswer: 'Save answer',
    enterFirst: 'Enter an answer first.',
    understood: 'Here’s what I understood',
    foundIn: (doc) => `Found in ${doc}`,
    sure: (pct) => `${pct}% sure`,
    page: (n) => `page ${n}`,
    rowState: {
      confirmed: 'Saved',
      declined: 'Not saved',
      expired: 'Not saved: replaced by a newer message',
    },
    confirm: 'Confirm',
    confirmLabel: (own, together, many) =>
      own
        ? together === 1
          ? 'Confirm the optional one'
          : `Confirm the ${together} optional ones`
        : many && together > 1
          ? `Confirm all ${together}`
          : 'Confirm',
    footer: {
      ownOnly: 'Confirm each answer above, or change them.',
      mixed: 'Answers that matter most are confirmed one by one.',
      document: 'Confirming saves what was read from your document.',
      chat: 'Confirming saves it as your own answer.',
    },
    change: 'Change',
    placeholder: 'Type a reply, or ask what something means',
    yourReply: 'Your reply',
    send: 'Send',
    busy: {
      send: 'Reading your reply',
      save: 'Saving',
      wait: 'One moment',
      language: 'Switching language',
      upload: 'Uploading and scanning',
    },
    ask: ['What does this mean?', 'Why does this matter?'],
  },
  ms: {
    points: (n) => `${n > 0 ? '+' : '−'}${Math.abs(n)} mata`,
    basedOn: 'Berdasarkan',
    tapped: 'Ditekan',
    form: 'Dijawab dalam borang',
    again: 'Kembali kepada soalan ini.',
    optional: 'Pilihan',
    killSwitchQuestion:
      'Jawapan ini boleh mengehadkan skor anda, jadi ia hanya disimpan apabila anda menekan atau mengesahkannya.',
    killSwitchCard:
      'Termasuk jawapan yang boleh mengehadkan skor anda, jadi tiada apa disimpan sehingga anda mengesahkannya.',
    stageDone: (name) => `${name} selesai`,
    doneTitle: (left) => (left ? 'Tiada soalan lagi buat masa ini' : 'Semua soalan wajib sudah dijawab'),
    doneText: (answered, left) =>
      `${answered} dijawab. ${left ? `${left} soalan wajib akan dibuka selepas peringkat terdahulu selesai.` : 'Soalan pilihan masih boleh memperhalusi keputusan anda.'}`,
    quickReplies: 'Jawapan pantas',
    pickAll: 'Pilih semua yang berkenaan',
    skip: 'Langkau',
    done: (n) => `Selesai${n ? ` (${n})` : ''}`,
    upload: 'Muat naik fail',
    uploadLater: (optional) =>
      `Muat naik fail tidak tersedia di sini${optional ? '. Langkau soalan ini buat masa ini.' : '.'}`,
    uploadHint: (kinds) => `${kinds}, sehingga 25 MB. Fail diimbas untuk perisian hasad sebelum disimpan.`,
    reviewDocs: (n, disagree) =>
      `${n} jawapan daripada dokumen anda untuk disemak${disagree ? `, ${disagree} yang bercanggah` : ''}`,
    typeBelow: 'Taip jawapan anda di bawah',
    useForm: 'Guna borang',
    saveAnswer: 'Simpan jawapan',
    enterFirst: 'Masukkan jawapan dahulu.',
    understood: 'Ini yang saya faham',
    foundIn: (doc) => `Ditemui dalam ${doc}`,
    sure: (pct) => `${pct}% pasti`,
    page: (n) => `halaman ${n}`,
    rowState: {
      confirmed: 'Disimpan',
      declined: 'Tidak disimpan',
      expired: 'Tidak disimpan: digantikan oleh mesej baharu',
    },
    confirm: 'Sahkan',
    confirmLabel: (own, together, many) =>
      own
        ? together === 1
          ? 'Sahkan jawapan pilihan'
          : `Sahkan ${together} jawapan pilihan`
        : many && together > 1
          ? `Sahkan semua ${together}`
          : 'Sahkan',
    footer: {
      ownOnly: 'Sahkan setiap jawapan di atas, atau ubahnya.',
      mixed: 'Jawapan yang paling penting disahkan satu demi satu.',
      document: 'Mengesahkan akan menyimpan apa yang dibaca daripada dokumen anda.',
      chat: 'Mengesahkan akan menyimpannya sebagai jawapan anda sendiri.',
    },
    change: 'Ubah',
    placeholder: 'Taip jawapan, atau tanya maksud sesuatu',
    yourReply: 'Jawapan anda',
    send: 'Hantar',
    busy: {
      send: 'Membaca jawapan anda',
      save: 'Menyimpan',
      wait: 'Sebentar',
      language: 'Menukar bahasa',
      upload: 'Memuat naik dan mengimbas',
    },
    ask: ['Apa maksudnya?', 'Kenapa ini penting?'],
  },
};
